/**
 * @fileOverview Contains code to pull app store reviews for a given app,
 * pull out positive reviews, and makes them available to the website.
 */

import axios from 'axios'

/**
 * @class AppReview
 *
 * @description Models a single app review.
 */
export class AppReview {
  constructor ({ author, country, rating, message, title } = {}) {
    this.author = author
    this.country = country
    this.rating = rating
    this.message = message
    this.title = title
  }
}

/**
 * @class AppReviewFetcher
 * @description Downloads app reviews from iTunes.
 *
 * @method fetchReviews() Fetches the reviews from the server for the specified app Id.
 *
 */
export class AppReviewFetcher {
  constructor ({ appId, countryCode = 'us' } = {}) {
    if (!appId) {
      throw new Error('App ID is required to initialize AppReviewFetcher.')
    }
    this.appId = appId
    this.url = `https://itunes.apple.com/${countryCode}/rss/customerreviews/id=${appId}/json`
  }

  /**
   * Fetches the reviews from the server for the specified app Id.
   * @memberOf AppReviewFetcher
   */
  fetchReviews () {
    return axios
      .get(this.url)
      .then(response => response.data)
      .then(data => data.feed)
      .then(feed => feed.entry)
      .then(entries => entries.slice(1))
      .then(reviews =>
        reviews.map(
          review =>
            new AppReview({
              author: review.author.name.label,
              country: review.author.uri.label.split(
                '/'
              )[3] /* The third element is country code. */,
              rating: parseInt(review['im:rating'].label, 10),
              title: review.title.label,
              message: review.content.label
            })
        )
      )
      .then(reviews => reviews.filter(review => review.rating > 4))
      .then(reviews =>
        reviews.sort((a, b) => a.message.length < b.message.length)
      )
  }
}
